import { useMutation } from '@tanstack/react-query'
import cx from 'classnames'
import _ from 'lodash'
import { useEffect } from 'react'
import { MdClose } from 'react-icons/md'
import { Link } from 'react-router-dom'
import ReactSelect from 'react-select'

import { SystemRoles } from '../../../../lib/Codes'
import http from '../../../../lib/http'
import title from '../../../../lib/title'
import { useAppDispatch, useAppSelector } from '../../../../store'
import { logout, setStatus } from '../../../LoginForm/actions'
import profileActions from '../../../Profile/actions'
import { Divider } from '../../../shared/Divider'
import { Toggle } from '../../../shared/NarusMaterial'
import { useSidebar } from '../../context'
import * as Sidebar from '../components/index'
import styles from '../sidebar.module.css'

interface ExtractReturnType {
  value: boolean
  draft: boolean
  row: { id: string }
  others: any[]
}

export const extract = (id: string, status: Record<string, unknown> | null | undefined, name: string): ExtractReturnType => {
  if (!status?.data) {
    return {
      value: false,
      draft: false,
      row: { id: '' },
      others: []
    }
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  return status?.data?.reduce((acc, row) => {
    if (row.id === id) {
      acc.row = _.pickBy(row, _.identity)
      acc.value = row[name]
      acc.draft = row[`draft_${name}`]
      if (row[name]) {
        acc.others.push(row)
      }
    } else if (row[name]) {
      acc.others.push(row)
    }
    return acc
  },
  {
    value: false,
    draft: false,
    row: { id },
    others: []
  })
}

function UserRoles ({ user }: {user: Record<string, unknown>}): JSX.Element {
  const role = _.get(user, 'app_metadata.roles.meta.role')

  const roles = SystemRoles as Array<{ value: string, label: string }>

  const roleDisplay =
      roles.find((r) => r.value === role)?.label ?? '...'

  return <span className={styles.userRole}>{roleDisplay}</span>
}

export default function UserName ({ user }: { user: Record<string, unknown> }): JSX.Element | null {
  const given = _.get(user, 'user_metadata.given')
  const family = _.get(user, 'user_metadata.family')
  const userName = [ given, family ].join(' ')

  const suffixes = _.get(user, 'user_metadata.suffix', []) as string[]
  const displaySuffix = suffixes.join(', ') ?? ''

  const userDisplayName = displaySuffix.length
    ? [ userName, displaySuffix ].join(', ')
    : userName

  if (!userDisplayName) {
    return null
  }

  return (
    <span className={styles.userName}>
      {userDisplayName}
    </span>
  )
}

interface UserToggleProps {
  name: string
  label: string
  id: string
  status?: Record<string, unknown> | null
  caption: string
  colorScheme?: 'green-red'
  showOthers?: boolean
}

const UserToggle = ({ name, label, id, status, caption, colorScheme, showOthers }: UserToggleProps): JSX.Element => {
  let dispatch = useAppDispatch()
  const handleToggle = (row: Record<string, unknown>, key: string, value: string | number | boolean): void => {
    dispatch(setStatus({
      ...row,
      [key]: value,
      [`draft_${key}`]: true
    }))
  }

  const extracted = extract(id, status, name)

  const others = extracted?.others
    ?.map((row: OtherUser) => `${row?.first_name} ${row?.last_name}${row?.suffix ? `, ${row?.suffix}` : ''}`)?.sort() ?? []

  const othersDisplay: string = others.slice(0, 5)?.join(', ') ?? ''
  const othersTitle = others.join('\n')

  return (
    <div className={styles.userSwitchWrapper}>
      <div className={styles.userSwitch}>
        <button
          onClick={() => handleToggle(extracted.row, name, !extracted.value)}
          className={styles.userSwitchLabel}

          title={caption}
        >
          {label}&nbsp;
          <span
            title={othersTitle}
            className={styles.others}
          >
            {others.length ?? ''}
          </span>
        </button>

        {status
          ? (
            <Toggle
              value={extracted.value || false}
              onChange={(value: string) => handleToggle(extracted.row, name, value)}
              colorScheme={colorScheme}
            />
          )
          : (
            <i className='load fa fa-spinner' />
          )
        }

      </div>
      {showOthers && othersDisplay.length > 0 && (
        <p
          title={othersTitle}
          className={styles.others}
        >
          {othersDisplay}
        </p>
      )}
    </div>
  )
}

function EncounterOpenPreferences (): JSX.Element {
  interface Profile {
    on_encounter_open: string | null
  }

  interface Variables {
    on_encounter_open: string | null
  }

  interface Option {
    value: string
    label: string
  }

  let profile: Profile | undefined = useAppSelector(state => (state.profile as any)?.profile)
  let isLoading: boolean = useAppSelector(state => (state.profile as any)?.loading ?? false)
  let dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(profileActions.get())
  }, [ dispatch ])

  let mutation = useMutation({
    async mutationFn (variables: Variables) {
      await http.putRequest('/profile', {
        ...profile,
        on_encounter_open: variables.on_encounter_open === 'null'
          ? null
          : variables.on_encounter_open
      })
    },

    onSuccess () {
      dispatch(profileActions.get())
    }
  })

  let options: Option[] = [
    { value: 'null', label: 'Do nothing' },
    { value: 'open-encounter-summary', label: 'Open Encounter Summary' },
    { value: 'open-chats', label: 'Open chats with the patient' }
  ]

  let value = options.find(o => o.value === (profile?.on_encounter_open ?? 'null'))

  return (
    <div className='px-[30px] py-3 pb-5 flex flex-col gap-3'>
      <label
        htmlFor='encounter-open-preferences'
      >
        <span className='uppercase text-xs font-semibold text-gray-400'>
          When opening an encounter
        </span>
      </label>

      <ReactSelect
        inputId='encounter-open-preferences'
        isLoading={isLoading || mutation.isPending}
        isDisabled={isLoading || mutation.isPending}
        value={value}
        options={options}
        onChange={option => {
          void mutation.mutate({
            on_encounter_open: option?.value === 'null'
              ? null
              : option?.value ?? null
          })
        }}
      />
    </div>
  )
}

interface OtherUser { first_name: string, last_name: string, suffix: string }

export function User (): JSX.Element {
  let { status, profile } = useAppSelector((state) => state.auth)
  const role = _.get(profile, 'app_metadata.roles.meta.role')
  const isAgent = [ 'broker_access', 'csr_agent' ].includes(role)
  let dispatch = useAppDispatch()
  let sidebar = useSidebar()

  const id = profile?.sub as string ?? ''

  return (
    <Sidebar.Wrapper>
      <div className={styles.userInfoWrapper}>
        <div className={styles.userInfo}>
          {profile && <UserName user={profile} />}
          {profile && <UserRoles user={profile} />}
        </div>
        <button
          style={{ cursor: 'pointer' }}
          onClick={sidebar.close}
          title='Close panel'
        >
          <MdClose
            color='black'
            size='1.4rem'
          />
        </button>
      </div>
      <div>
        <UserToggle
          colorScheme='green-red'
          showOthers
          name='onduty'
          label='On Duty'
          id={id}
          status={status}
          caption={title.onDuty}
        />
      </div>
      {!isAgent && (
        <>
          <Divider />
          <UserToggle
            name='monitoring'
            label='Monitoring'
            id={id}
            status={status}
            caption={title.monitoring}
          />
          <Divider />
          <UserToggle
            name='oncall'
            label='On Call'
            id={id}
            status={status}
            caption={title.onCall}
          />
        </>
      )}
      <Divider />
      <UserToggle
        name='concierge'
        label='Concierge'
        id={id}
        status={status}
        caption={title.concierge}
      />
      <Divider />
      <EncounterOpenPreferences />
      <Divider />
      <div style={{ display: 'flex', flexDirection: 'column', marginTop: '20px' }}>
        <Link
          className={styles.userRow}
          to='/profile'
        >
          Profile
        </Link>

        <button
          type='button'
          className={styles.userRow}
          onClick={() => {
            dispatch(logout(null, sidebar.close))
          }}
        >
          Sign Out
        </button>
      </div>
    </Sidebar.Wrapper>
  )
}

export function UserIcon (): JSX.Element {
  return (
    <div className={styles.userStatusFirst}>
      <div className={cx(styles.userStatusSecond)}>
        <div className={cx(styles.userStatusThird)} />
      </div>
    </div>
  )
}
